import React from "react";
import styled from "styled-components";
import Image from "../../atoms/Image";
import { DrugSearchResult } from "../../../common/models/types";
import { DrugSearchResultItem } from "../../../api/drugIdentificationService";

// DrugSearchResult 또는 DrugSearchResultItem 둘 다 지원
type ResultItem = DrugSearchResult | DrugSearchResultItem;

interface DrugSearchResultListProps {
  results: ResultItem[];
  selectedItemCode?: string;
  isConfirmed?: boolean;
  maxHeight?: string;
  title?: string;
  disableScroll?: boolean; // true일 경우 내부 스크롤 없이 전체 높이로 표시
}

// 스타일
const Section = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: visible;
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const ResultListWrapper = styled.div<{ $maxHeight?: string; $disableScroll?: boolean }>`
  ${props => !props.$disableScroll && `
    max-height: ${props.$maxHeight || '600px'};
    overflow-y: auto;
  `}
  overflow-x: visible;
`;

const ResultItem = styled.div<{ $selected?: boolean; $confirmed?: boolean }>`
  margin-bottom: 8px;
  border-radius: 8px;
  border: 2px solid ${props => props.$confirmed ? '#059669' : props.$selected ? '#4A90E2' : '#e5e7eb'};
  background: ${props => props.$confirmed ? '#ECFDF5' : props.$selected ? '#EBF5FF' : 'white'};
  font-size: 14px;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ResultItemContent = styled.div`
  display: flex;
  align-items: center;
`;

const ResultItemImageWrapper = styled.div`
  flex-shrink: 0;
  width: 140px;
  background: #f9fafb;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Image 컴포넌트 및 내부 요소 여백 제거 */
  & > div {
    display: flex;
    line-height: 0;
  }

  img {
    max-width: 140px;
    max-height: 72px;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
  }
`;

const ResultItemInfoContainer = styled.div`
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
`;

const ResultItemName = styled.div`
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
  word-break: break-word;
`;

const ResultItemInfo = styled.div`
  color: #6b7280;
  font-size: 12px;
  word-break: break-word;
`;

const ConfirmedBadge = styled.span`
  background: #059669;
  color: white;
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 4px;
  margin-left: auto;
  flex-shrink: 0;
`;

const NoResult = styled.div`
  color: #6b7280;
  font-size: 14px;
  text-align: center;
  padding: 40px;
`;

const DrugSearchResultList: React.FC<DrugSearchResultListProps> = ({
  results,
  selectedItemCode,
  isConfirmed = false,
  maxHeight,
  title = "검색 결과",
  disableScroll = false
}) => {
  return (
    <Section>
      <SectionTitle>{title} ({results.length}건)</SectionTitle>
      <ResultListWrapper $maxHeight={maxHeight} $disableScroll={disableScroll}>
        {results.length > 0 ? (
          results.map((drug, index) => {
            const itemCode = drug.itemSeq;
            const isSelected = selectedItemCode === itemCode;
            const isItemConfirmed = isSelected && isConfirmed;

            return (
              <ResultItem key={itemCode || index} $selected={isSelected} $confirmed={isItemConfirmed}>
                <ResultItemContent>
                  <ResultItemImageWrapper>
                    {drug.itemImage ? (
                      <Image
                        src={drug.itemImage}
                        alt={drug.itemName}
                        height={72}
                        objectFit="contain"
                        enableHoverZoom={true}
                        zoomScale={6}
                        showOnError={false}
                      />
                    ) : null}
                  </ResultItemImageWrapper>
                  <ResultItemInfoContainer>
                    <ResultItemName>
                      {drug.itemName}
                    </ResultItemName>
                    <ResultItemInfo>
                      {drug.entpName} | {drug.formulation} | {drug.shape}
                    </ResultItemInfo>
                    <ResultItemInfo>
                      색상: {drug.colorClass1}{drug.colorClass2 && `/${drug.colorClass2}`}
                    </ResultItemInfo>
                  </ResultItemInfoContainer>
                  {isItemConfirmed && <ConfirmedBadge>확정</ConfirmedBadge>}
                </ResultItemContent>
              </ResultItem>
            );
          })
        ) : (
          <NoResult>검색 결과가 없습니다.</NoResult>
        )}
      </ResultListWrapper>
    </Section>
  );
};

export default DrugSearchResultList;
